// src/pages/AdminLoginPage.js
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AdminContext } from '../context/AdminContext';
import { ShieldAlert, KeyRound, User } from 'lucide-react';
import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

function AdminLoginPage() {
    const [nome, setNome] = useState('');
    const [senha, setSenha] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { adminLogin, isAdmin } = useContext(AdminContext);
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || "/";

    useEffect(() => {
        if (isAdmin) {
            navigate(from, { replace: true });
        }
    }, [isAdmin, navigate, from]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await adminLogin(nome, senha);
        
        if (result.success) {
            navigate(from, { replace: true });
        } else {
            setError(result.message);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-32 relative overflow-hidden font-sans text-stone-800 flex items-center justify-center">
            <title>{`${Config.SITE_TITLE} - Acesso Restrito`}</title>
            <link rel="icon" href={ICON} />
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

            <div className="max-w-md w-full mx-4 relative z-10 animate-in fade-in zoom-in-95 duration-500">
                <div className="bg-white/80 backdrop-blur-sm p-8 md:p-10 rounded-[2rem] border border-[#dbcdac]/40 shadow-xl text-center">
                    
                    <div className="w-16 h-16 bg-[#971253]/10 rounded-full flex items-center justify-center mx-auto mb-6 text-[#971253]">
                        <ShieldAlert size={32} />
                    </div>
                    
                    <h1 className="font-serif text-3xl font-bold text-[#971253] mb-2">Acesso Restrito</h1>
                    <p className="text-stone-500 text-sm mb-8 font-light">
                        Área de desenvolvimento e administração. Insira suas credenciais para visualizar páginas em preview.
                    </p>

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl font-medium">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5 text-left">
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-stone-600 uppercase tracking-widest ml-1">Usuário</label>
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400">
                                    <User size={20} />
                                </div>
                                <input 
                                    type="text" 
                                    value={nome}
                                    onChange={(e) => setNome(e.target.value)}
                                    className="w-full bg-[#FFF5EE]/50 border border-[#dbcdac]/60 rounded-xl py-3 pl-12 pr-4 text-stone-700 focus:outline-none focus:border-[#971253] focus:ring-2 focus:ring-[#971253]/10 transition-all"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-bold text-stone-600 uppercase tracking-widest ml-1">Senha</label>
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400">
                                    <KeyRound size={20} />
                                </div>
                                <input 
                                    type="password" 
                                    value={senha}
                                    onChange={(e) => setSenha(e.target.value)}
                                    className="w-full bg-[#FFF5EE]/50 border border-[#dbcdac]/60 rounded-xl py-3 pl-12 pr-4 text-stone-700 focus:outline-none focus:border-[#971253] focus:ring-2 focus:ring-[#971253]/10 transition-all"
                                    required
                                />
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading}
                            className="w-full mt-4 py-4 bg-[#971253] text-[#FFF5EE] rounded-xl font-bold tracking-wider hover:bg-[#7a0e42] transition-all shadow-md disabled:opacity-70 disabled:hover:translate-y-0 hover:-translate-y-0.5 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                "Desbloquear Sistema"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default AdminLoginPage;